import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    X, FileText, FileType, Presentation, Download,
    History, FileDown, CheckCircle2, MoreVertical,
    MessageSquare, FileEdit, Globe, File, Palette, Edit
} from 'lucide-react';
import './ExportModal.css';
import CustomLoader from '../../../../shared/components/ui/CustomLoader';
import SuccessToast from '../../../../shared/components/ui/SuccessToast';
import api from '../../../../services/api';
import { useThemes } from '../../../themes/hooks/useThemes';
import { useDocumentProgress } from '../hooks/useDocumentProgress';
import { useSlideGeneration } from '../hooks/useSlideGeneration';
import { useRealTimeChat } from '../../../chat/context/RealTimeChatContext';
import { extractFilenameFromHeaders } from '../utils/documentProgress';

const ExportModal = ({ rfpId, analysisData, isManager, onClose = () => { }, onPPTClick = () => { }, showToast }) => {
    const navigate = useNavigate();
    const { subscribeToMessages } = useRealTimeChat();
    const { themes, isLoading: loadingThemes } = useThemes();
    // Form States
    const [selectedFormat, setSelectedFormat] = useState('pdf');
    const [selectedLang, setSelectedLang] = useState('en');
    const [selectedThemeId, setSelectedThemeId] = useState('');
    const [exportName, setExportName] = useState('');
    const [exportComment, setExportComment] = useState('');

    // Action States
    const [downloading, setDownloading] = useState(false);
    const [error, setError] = useState('');

    // History States
    const [versions, setVersions] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(true);
    const [historyError, setHistoryError] = useState('');
    const [toast, setToast] = useState(null);
    const [downloadingHistoryId, setDownloadingHistoryId] = useState(null);
    const [selectedVersions, setSelectedVersions] = useState(new Set());
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // The progress dialog opens only when the server really generates the document. If it was
    // generated before, the user just gets a short notice and the download starts.
    const showNotice = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3000);
    };
    const { ensureSlidesGenerated } = useSlideGeneration();
    const {
        showProgress: showGenerationProgress,
        percent: translationProgressPct,
        message: translationProgressMsg,
    } = useDocumentProgress({
        rfpId,
        isActive: downloading,
        onReady: (message) => showNotice(message),
    });

    useEffect(() => {
        if (rfpId) {
            fetchHistory();
        }
    }, [rfpId]);

    // Listen for DOWNLOAD_COMPLETE: show server's success message as a toast.
    useEffect(() => {
        const unsubscribe = subscribeToMessages((payload) => {
            if (payload.type === 'DOWNLOAD_COMPLETE' && String(payload.rfp_id) === String(rfpId)) {
                showNotice(payload.message || 'Report downloaded successfully!', 'success');
                fetchHistory(); // refresh history list so the new version appears
            }
        });
        return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subscribeToMessages, rfpId]);

    // Prevent background scrolling when modal is open
    useEffect(() => {
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = originalOverflow;
        };
    }, []);

    // Set default theme when themes load (prioritizing template_id from backend analysisData)
    useEffect(() => {
        if (themes.length > 0 && !selectedThemeId) {
            const backendThemeId = analysisData?.template_id || analysisData?.rfp_details?.template_id || analysisData?.metadata?.template_id || analysisData?.template?.template_id || analysisData?.template?.id;

            if (backendThemeId && themes.some(t => String(t.id) === String(backendThemeId))) {
                setSelectedThemeId(backendThemeId);
            } else {
                setSelectedThemeId(themes[0].id);
            }
        }
    }, [themes, selectedThemeId, analysisData]);

    const fetchHistory = async () => {
        try {
            setLoadingHistory(true);
            const response = await api.get(`/rfp/${rfpId}/report/versions`);
            if (response.data && response.data.versions) {
                setVersions(response.data.versions);
            }
        } catch (err) {
            console.error('Error fetching history:', err);
            setHistoryError('Failed to load export history.');
        } finally {
            setLoadingHistory(false);
        }
    };

    const handleGenerateDownload = async () => {
        setError('');

        if (!rfpId) {
            setError('RFP ID is missing. Please try again.');
            return;
        }

        setDownloading(true);
        try {
            // A PowerPoint needs the slides. If they were never generated, generate them first
            // (the progress dialog shows the live messages), then download.
            if (selectedFormat === 'pptx') {
                await ensureSlidesGenerated({ rfpId, templateId: selectedThemeId });
            }

            const name = encodeURIComponent(exportName);
            const comment = encodeURIComponent(exportComment);



            let url = `/rfp/report/${rfpId}?format=${selectedFormat}&language=${selectedLang}`;
            if (name) url += `&name=${name}`;
            if (comment) url += `&comment=${comment}`;
            if (selectedThemeId) {
                url += `&template_id=${selectedThemeId}`;
            }

            const requestOptions = {
                responseType: 'blob',
                headers: {
                    'rfp_id': String(rfpId),
                    'Accept-Language': selectedLang,
                    'language': selectedLang
                }
            };

            // PowerPoint is downloaded with POST /rfp/report/{rfp_id} (GET only if the server says 405).
            let response;
            if (selectedFormat === 'pptx') {
                try {
                    response = await api.post(url, {}, requestOptions);
                } catch (postError) {
                    if (postError.response?.status !== 405) throw postError;
                    response = await api.get(url, requestOptions);
                }
            } else {
                response = await api.get(url, requestOptions);
            }

            // Download the file
            const blob = new Blob([response.data]);
            const ext = selectedFormat === 'docx' ? 'docx' : selectedFormat === 'pptx' ? 'pptx' : 'pdf';
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            // Prefer the backend's clean filename from Content-Disposition; fall back to local name.
            const defaultFilename = `${exportName || 'RFP_Report'}_${rfpId}.${ext}`;
            a.download = extractFilenameFromHeaders(response, defaultFilename);
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(downloadUrl);

            // Show success message from x-download-message header if available
            const downloadMsg = response.headers?.['x-download-message'];
            if (downloadMsg) showNotice(downloadMsg, 'success');

            // Refresh history after a successful download
            fetchHistory();
        } catch (err) {
            console.error('Download error:', err);
            const detail = err.response?.data?.detail;
            setError(`Generation failed: ${(typeof detail === 'string' && detail) || err.message}`);
        } finally {
            setDownloading(false);
        }
    };

    const handleDownloadHistory = async (version) => {
        setDownloadingHistoryId(version.version_number || version.id);
        try {
            // GET /rfp/{rfp_id}/report/versions/{version_number}/download
            // -> { download_url, filename }: a link to the saved file and its clean name.
            const response = await api.get(`/rfp/${rfpId}/report/versions/${version.version_number}/download`);
            const { download_url: downloadUrl, filename } = response.data || {};
            if (!downloadUrl) throw new Error('The server did not return a download link.');

            const ext = version.format === 'docx' ? 'docx' : version.format === 'pptx' ? 'pptx' : 'pdf';
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.setAttribute('download', filename || `Report_v${version.version_number}.${ext}`);
            document.body.appendChild(a);
            a.click();
            a.remove();
        } catch (err) {
            console.error('Download error:', err);
            const detail = err.response?.data?.detail;
            showNotice(typeof detail === 'string' && detail ? detail : 'Failed to download this historical version.', 'error');
        } finally {
            setDownloadingHistoryId(null);
        }
    };

    const getFormatIcon = (format) => {
        switch (format) {
            case 'pdf': return <FileText size={18} className="format-icon-pdf" />;
            case 'docx': return <FileType size={18} className="format-icon-word" />;
            case 'pptx': return <Presentation size={18} className="format-icon-ppt" />;
            default: return <FileText size={18} />;
        }
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const allVersionNumbers = versions.map(v => v.version_number);
            setSelectedVersions(new Set(allVersionNumbers));
        } else {
            setSelectedVersions(new Set());
        }
    };

    const handleSelectVersion = (versionNumber) => {
        const newSet = new Set(selectedVersions);
        if (newSet.has(versionNumber)) {
            newSet.delete(versionNumber);
        } else {
            newSet.add(versionNumber);
        }
        setSelectedVersions(newSet);
    };

    const handleDeleteVersions = async () => {
        setIsDeleting(true);
        try {
            const versionNumbers = Array.from(selectedVersions);
            const response = await api.delete(`/rfp/${rfpId}/report/versions`, {
                headers: { rfp_id: rfpId },
                data: { version_numbers: versionNumbers }
            });
            if (response.status === 200 || response.status === 204) {
                const numDeleted = versionNumbers.length;
                setSelectedVersions(new Set());
                fetchHistory();

                // Show toast with message from backend
                const toastMsg = response.data?.message || 'Successfully deleted version(s)';
                setToast({ message: toastMsg, type: 'success' });
                setTimeout(() => setToast(null), 3000);
            }
        } catch (error) {
            console.error('Failed to delete versions', error);
            setHistoryError(error.response?.data?.detail || error.response?.data?.message || 'Failed to delete selected versions.');
        } finally {
            setIsDeleting(false);
            setShowDeleteConfirm(false);
        }
    };

    return (
        <div className="export-modal-overlay" onClick={onClose}>
            <SuccessToast toast={toast} onClose={() => setToast(null)} />
            <div className="export-modal-two-col" onClick={e => e.stopPropagation()}>

                {/* Left Column: Generate Options */}
                <div className="export-modal-col-left">
                    <div className="export-modal-header">
                        <div className="export-modal-header-icon-container">
                            <FileDown size={24} color="#FF5526" />
                        </div>
                        <div className="export-modal-title">
                            <h2>Generate Response Document</h2>
                            <p>Configure your document before exporting. You can also view previously generated files from the export history.</p>
                        </div>
                    </div>

                    <button className="export-close-btn mobile-close" onClick={onClose}>
                        <X size={18} />
                    </button>

                    {error && <div className="export-error">{error}</div>}

                    <div className="export-section">
                        <h3 className="export-section-title">Select Format</h3>
                        <div className="export-format-grid">
                            {/* PDF Card */}
                            <div
                                className={`export-format-card ${selectedFormat === 'pdf' ? 'active' : ''}`}
                                onClick={() => setSelectedFormat('pdf')}
                            >
                                <div className="format-card-header">
                                    <div className="format-card-icon pdf-bg">
                                        <FileText size={20} color="#EF4444" />
                                    </div>
                                    <div className="format-card-radio">
                                        {selectedFormat === 'pdf' ? <CheckCircle2 size={18} color="#FF5526" fill="#FFF1EC" /> : <div className="radio-circle"></div>}
                                    </div>
                                </div>
                                <div className="format-card-title">PDF</div>
                                <div className="format-card-desc">Best for sharing and printing</div>
                            </div>

                            {/* Word Card */}
                            <div
                                className={`export-format-card ${selectedFormat === 'docx' ? 'active' : ''}`}
                                onClick={() => setSelectedFormat('docx')}
                            >
                                <div className="format-card-header">
                                    <div className="format-card-icon word-bg">
                                        <FileType size={20} color="#FF5526" />
                                    </div>
                                    <div className="format-card-radio">
                                        {selectedFormat === 'docx' ? <CheckCircle2 size={18} color="#FF5526" fill="#FFF1EC" /> : <div className="radio-circle"></div>}
                                    </div>
                                </div>
                                <div className="format-card-title">Word</div>
                                <div className="format-card-desc">Editable document format</div>
                            </div>

                            {/* PPT Card */}
                            <div
                                className={`export-format-card ${selectedFormat === 'pptx' ? 'active' : ''}`}
                                onClick={() => setSelectedFormat('pptx')}
                            >
                                <div className="format-card-header">
                                    <div className="format-card-icon ppt-bg">
                                        <Presentation size={20} color="#F59E0B" />
                                    </div>
                                    <div className="format-card-radio">
                                        {selectedFormat === 'pptx' ? <CheckCircle2 size={18} color="#FF5526" fill="#FFF1EC" /> : <div className="radio-circle"></div>}
                                    </div>
                                </div>
                                <div className="format-card-title">PowerPoint</div>
                                <div className="format-card-desc">Presentation-ready slides</div>
                            </div>
                        </div>
                    </div>

                    {/* Theme Selection */}
                    <div className="export-section">
                        <div className="export-section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                            <h3 className="export-section-title" style={{ margin: 0 }}>Theme Details </h3>
                            <button className="export-edit-theme-btn" onClick={() => navigate('/themes')} title="Edit Theme">
                                Edit<Edit size={16} />
                            </button>
                        </div>

                        <div className="export-theme-container">
                            {loadingThemes ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#64748B', fontSize: '0.8rem' }}>
                                    <CustomLoader size={16} /> Loading themes...
                                </div>
                            ) : (
                                <>
                                    <div className="export-form-group" style={{ marginBottom: '1rem' }}>
                                        <label>Change Theme</label>
                                        <div className="export-input-wrapper">
                                            <select 
                                                value={selectedThemeId} 
                                                onChange={(e) => setSelectedThemeId(e.target.value)}
                                                className="export-select"
                                            >
                                                {themes.length === 0 && <option value="">No themes available</option>}
                                                {themes.map(t => (
                                                    <option key={t.id} value={t.id}>{t.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    {selectedThemeId && themes.find(t => t.id === selectedThemeId) && (
                                        (() => {
                                            const theme = themes.find(t => t.id === selectedThemeId);
                                            return (
                                                <div className="export-theme-preview-grid">
                                                    <div className="theme-preview-grid-item">
                                                        <span className="theme-grid-label">Current Theme :</span>
                                                        <span className="theme-grid-value" title={theme.name}>{theme.name}</span>
                                                    </div>
                                                    <div className="theme-preview-grid-item">
                                                        <span className="theme-grid-label">Logo :</span>
                                                        {theme.clientLogoUrl ? (
                                                            <img src={theme.clientLogoUrl} alt="Logo" className="theme-grid-logo" />
                                                        ) : (
                                                            <span className="theme-grid-value text-muted">None</span>
                                                        )}
                                                    </div>
                                                    <div className="theme-preview-grid-item">
                                                        <span className="theme-grid-label">Brand Color :</span>
                                                        <div className="theme-grid-color-wrapper">
                                                            <div className="theme-grid-color-swatch" style={{ background: theme.primaryColor }}></div>
                                                            {/* <span className="theme-grid-value">{theme.primaryColor}</span> */}
                                                        </div>
                                                    </div>
                                                    <div className="theme-preview-grid-item">
                                                        <span className="theme-grid-label">Accent Color :</span>
                                                        <div className="theme-grid-color-wrapper">
                                                            <div className="theme-grid-color-swatch" style={{ background: theme.secondaryColor }}></div>
                                                            {/* <span className="theme-grid-value">{theme.secondaryColor}</span> */}
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                        })()
                                    )}
                                </>
                            )}
                        </div>
                    </div>

                    <div className="export-section">
                        <h3 className="export-section-title">Export Settings</h3>

                        <div className="export-form-group">
                            <label>Language</label>
                            <div className="export-input-wrapper">
                                <Globe size={16} className="export-input-icon" />
                                <select
                                    value={selectedLang}
                                    onChange={(e) => setSelectedLang(e.target.value)}
                                    className="export-select"
                                >
                                    <option value="en">English</option>
                                    <option value="ar">Arabic</option>
                                    <option value="es">Spanish</option>
                                    <option value="pt">Portuguese</option>
                                </select>
                            </div>
                        </div>

                        <div className="export-form-group">
                            <label>Document Name</label>
                            <div className="export-input-wrapper">
                                <input
                                    type="text"
                                    value={exportName}
                                    onChange={(e) => setExportName(e.target.value)}
                                    className="export-input"
                                    placeholder="e.g. Final Proposal"
                                    maxLength={100}
                                    style={{ paddingLeft: '0.8rem' }}
                                />
                                <span className="export-char-count">{exportName.length}/100</span>
                            </div>
                        </div>

                        <div className="export-form-group">
                            <label>Version Comment <span>(Optional)</span></label>
                            <div className="export-input-wrapper align-top">
                                <textarea
                                    value={exportComment}
                                    onChange={(e) => setExportComment(e.target.value)}
                                    className="export-textarea"
                                    placeholder="e.g. Updated pricing and compliance sections"
                                    maxLength={500}
                                    rows={2}
                                    style={{ paddingLeft: '0.8rem' }}
                                />
                                <span className="export-char-count bottom-right">{exportComment.length}/500</span>
                            </div>
                        </div>
                    </div>

                    <div className="export-modal-footer">
                        <button className="export-btn-outline" onClick={onClose}>
                            Cancel
                        </button>
                        <button
                            className="export-btn-primary"
                            onClick={handleGenerateDownload}
                            disabled={downloading}
                        >
                            {downloading ? <CustomLoader size={16} color="white" /> : <Download size={16} />}
                            <span>Generate & Download</span>
                        </button>
                    </div>
                </div>

                {/* Right Column: History */}
                <div className="export-modal-col-right">
                    <div className="history-header-row">
                        <div className="history-header-title">
                            <History size={18} color="#FF5526" />
                            <h3>Export History</h3>
                        </div>
                        {/* Removed unused View All button */}
                    </div>
                    <p className="history-subtitle">Previously generated reports</p>

                    {isManager && versions.length > 0 && selectedVersions.size > 0 && (
                        <div className="history-bulk-actions" style={{ animation: 'fadeIn 0.2s ease-out' }}>
                            <label className="history-select-all">
                                <input
                                    type="checkbox"
                                    checked={selectedVersions.size === versions.length}
                                    onChange={handleSelectAll}
                                />
                                <span>Select All</span>
                            </label>
                            <button className="history-delete-btn" onClick={() => setShowDeleteConfirm(true)}>
                                Delete ({selectedVersions.size})
                            </button>
                        </div>
                    )}

                    {historyError && <div className="export-error" style={{ marginBottom: '1rem' }}>{historyError}</div>}

                    <div className="history-list-container">
                        {loadingHistory ? (
                            <div className="history-state-center">
                                <CustomLoader size={24} />
                                <span>Loading history...</span>
                            </div>
                        ) : versions.length === 0 ? (
                            <div className="history-state-center text-muted">
                                <History size={32} opacity={0.5} />
                                <p>No historical versions found.</p>
                            </div>
                        ) : (
                            <div className="history-items">
                                {versions.map((version, index) => (
                                    <div key={`${version.id || 'no_id'}_${version.version_number || 'no_v'}_${index}`} className={`history-card ${selectedVersions.has(version.version_number) ? 'selected' : ''}`}>
                                        {isManager && (
                                            <div className="history-card-checkbox">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedVersions.has(version.version_number)}
                                                    onChange={() => handleSelectVersion(version.version_number)}
                                                />
                                            </div>
                                        )}
                                        <div className="history-card-icon-wrapper">
                                            {getFormatIcon(version.format)}
                                        </div>
                                        <div className="history-card-content">
                                            <div className="history-card-top">
                                                <div className="history-card-name">
                                                    {version.name || 'Untitled Report'}
                                                    <span className="history-version-pill">v{version.version_number}</span>
                                                </div>
                                            </div>
                                            <div className="history-card-meta">
                                                <span>{version.format.toUpperCase()}</span>
                                                <span className="meta-dot">&bull;</span>
                                                <span>{new Date(version.created_at.endsWith('Z') ? version.created_at : version.created_at + 'Z').toLocaleString('en-US', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}</span>
                                            </div>
                                            {version.comment && (
                                                <div className="history-card-comment">
                                                    {version.comment}
                                                </div>
                                            )}
                                        </div>
                                        <div className="history-card-actions">
                                            <button
                                                className="history-action-btn"
                                                onClick={() => handleDownloadHistory(version)}
                                                disabled={downloadingHistoryId === (version.version_number || version.id)}
                                                title="Download"
                                            >
                                                {downloadingHistoryId === (version.version_number || version.id) ? <CustomLoader size={16} /> : <Download size={16} />}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Desktop Close Button (absolute top right of the whole modal) */}
                <button className="export-close-btn desktop-close" onClick={onClose}>
                    <X size={20} />
                </button>
            </div>

            {/* Download Progress Modal */}
            {downloading && showGenerationProgress && (
                <div className="status-modal-overlay" style={{ zIndex: 10001 }} onClick={e => e.stopPropagation()}>
                    <div className="status-modal-content" style={{ padding: '2.5rem', maxWidth: '400px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <CustomLoader size={40} />
                        <h3 style={{ marginTop: '1.5rem', marginBottom: '0.5rem', color: '#1E293B' }}>Generating Document</h3>
                        <p style={{ textAlign: 'center', marginBottom: '1.5rem', color: '#64748B', fontSize: '0.9rem' }}>
                            {translationProgressMsg}
                        </p>
                        
                        <div style={{ width: '100%', backgroundColor: '#E2E8F0', borderRadius: '9999px', height: '8px', overflow: 'hidden' }}>
                            <div 
                                style={{ 
                                    height: '100%', 
                                    backgroundColor: '#FF5526', 
                                    width: `${translationProgressPct}%`,
                                    transition: 'width 0.3s ease-in-out'
                                }} 
                            />
                        </div>
                        <div style={{ alignSelf: 'flex-end', fontSize: '0.85rem', color: '#64748B', marginTop: '0.5rem', fontWeight: 600 }}>
                            {translationProgressPct}%
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="status-modal-overlay" style={{ zIndex: 10001 }} onClick={e => e.stopPropagation()}>
                    <div className="status-modal-content" style={{ padding: '2rem', maxWidth: '400px' }}>
                        <div className="status-modal-icon">
                            <History size={32} color="#ef4444" />
                        </div>
                        <p style={{ textAlign: 'center', margin: '1rem 0', fontWeight: '500', color: '#1E293B' }}>
                            Are you sure you want to delete {selectedVersions.size} selected history item(s)? This action cannot be undone.
                        </p>
                        <div className="status-modal-actions" style={{ display: 'flex', gap: '10px', width: '100%', marginTop: '1rem', justifyContent: 'center' }}>
                            <button
                                className="status-modal-cancel"
                                onClick={() => setShowDeleteConfirm(false)}
                                style={{ flex: 1, background: '#F1F5F9', color: '#475569', border: 'none', padding: '0.6rem 1rem', borderRadius: '10px', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s' }}
                                disabled={isDeleting}
                            >
                                Cancel
                            </button>
                            <button
                                className="status-modal-close"
                                onClick={handleDeleteVersions}
                                style={{ flex: 1, background: '#ef4444', color: 'white', padding: '0.6rem 1.5rem', border: 'none', borderRadius: '10px', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer' }}
                                disabled={isDeleting}
                            >
                                {isDeleting ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ExportModal;
